import type { Empresa } from '@/types/types';
import { normalizeAsesoriaEstado } from '@/utils/asesoriaStatus';
import { api } from './api';

export interface EmpresaListResponse {
  empresas: Empresa[];
  success: boolean;
  message?: string;
}

export interface EmpresaDetailResponse {
  empresa: Empresa;
}

export interface EmpresaHistorialResponse {
  success: boolean;
  historial: Historial[];
}

export interface Historial {
  id: number;
  empresaId: string;
  asesoriaId: number;
  mes: number;
  anio: number;
  porcentajeCumplimiento: number;
  horasAsesoria: number;
  ntcsJson?: string | null;
  fechaRegistro: Date;
}

export interface ActualizarCorreoEmpresaRequest {
  correoEmpresa: string;
}

export interface ActualizarCorreoEmpresaResponse {
  success: boolean;
  message: string;
  data?: { correoEmpresa: string };
}

export const empresasService = {
  normalizeEmpresa(raw: Empresa & Record<string, unknown>): Empresa {
    const estadoRaw =
      (raw.asesoriaActivaEstado as string | null | undefined) ??
      (raw.estadoAsesoriaActiva as string | null | undefined) ??
      (raw.asesoriaEstadoActiva as string | null | undefined);

    return {
      ...raw,
      asesoriaActivaEstado: estadoRaw ? normalizeAsesoriaEstado({ estado: estadoRaw }) : null,
    };
  },

  async getAll(): Promise<Empresa[]> {
    const data = await api.get<EmpresaListResponse>('/empresas');
    if (!data.success) {
      throw new Error(data.message || 'Error al obtener la lista de empresas');
    }
    return data.empresas.map((empresa) => this.normalizeEmpresa(empresa as Empresa & Record<string, unknown>));
  },

  async getByNit(nit: string): Promise<Empresa> {
    const data = await api.get<EmpresaDetailResponse>(`/empresas/${nit}`);
    return this.normalizeEmpresa(data.empresa as Empresa & Record<string, unknown>);
  },

  async getHistorialEmpresa(nit: string): Promise<EmpresaHistorialResponse> {
    const data = await api.get<EmpresaHistorialResponse>(`/empresas/${nit}/historial`);
    return data;
  },

  async actualizarCorreoEmpresa(nit: string, correoEmpresa: string): Promise<ActualizarCorreoEmpresaResponse> {
    return api.patch<ActualizarCorreoEmpresaResponse>(`/empresas/${nit}/email`, { correoEmpresa });
  },

  async actualizarDetalleEmpresa(nit: string, data: Partial<Empresa>): Promise<{ success: boolean; message: string; data?: unknown }> {
    return api.patch<{ success: boolean; message: string; data?: unknown }>(`/empresas/${nit}/detalle`, data);
  },

  async obtenerAsesoriasDesincronizadas(
    nit: string
  ): Promise<{ success: boolean; desincronizadas: Array<{ asesoriaId: number; estado: string; asesorId: number; objetivo: string }> }> {
    return api.get(`/empresas/${nit}/asesorias-desincronizadas`);
  },

  async sincronizarAsesorias(nit: string): Promise<{ success: boolean; sincronizadas: number }> {
    return api.post(`/empresas/${nit}/sincronizar-asesorias`, {});
  },
};
